<script setup lang="ts">
import type { Product } from '~/models/Product'
import { TeleportationTarget } from '~/models/TeleportationTarget'
import { computed, ref } from 'vue'
import arrayUtils from '~/utils/arrayUtils'
import { useAuthStore } from '~/stores/auth'
import { storeToRefs } from 'pinia'
import useProductStore from '~/stores/product'
import { KsButton } from '@aschehoug/kloss'
import { sortByNameGradeSubject } from '~/utils/productSorter'
import type { SubjectCode } from '~/models/Subject'
import { highSchoolAdultAndPrepGrades } from '~/utils/gradeSorter'
import router from '~/router'
import { useI18n } from 'vue-i18n'
import useSubjectsStore from '~/stores/subjects'
import { sortBySubjectIndex } from '~/utils/subjectSorter'
import useSlugify from '~/composables/useSlugify'

const { t } = useI18n()
const { unique, intersect } = arrayUtils()
const authStore = useAuthStore()
const {
  userSubjects,
  userRelevantGrades,
  userRelevantSubjects,
  userSubjectsByGrades,
} = storeToRefs(authStore)
const { subjects } = storeToRefs(useSubjectsStore())
const { addonProducts, highSchoolProducts } = storeToRefs(useProductStore())
const { slugify } = useSlugify()

const showAllSubjects = ref(false)
const showAllHighSchoolProducts = ref(false)
const showAllAddonProducts = ref(false)

const hasHighSchoolGrades = computed(() => userRelevantGrades.value
  .some((grade) => highSchoolAdultAndPrepGrades.includes(grade)))

const isHighSchoolOnly = computed(() => userRelevantGrades.value
  .every((grade) => highSchoolAdultAndPrepGrades.includes(grade)))

const relevantSubjects = computed((): SubjectCode[] => {
  if (isHighSchoolOnly.value) return []
  if (showAllSubjects.value) return userSubjectsByGrades.value
  return userRelevantSubjects.value.length ? userRelevantSubjects.value : subjects.value
})

const isRelevantProduct = (product: Product) => !product.grades.length
  || intersect(product.grades, userRelevantGrades.value).length > 0

const isRelevantHighSchoolProduct = (product: Product) => isRelevantProduct(product)
  && (!product.subjects.length || intersect(product.subjects, userSubjects.value).length > 0)

const relevantAddonProducts = computed(() => [...addonProducts.value]
  .filter((product) => showAllAddonProducts.value || isRelevantProduct(product))
  .sort(sortByNameGradeSubject)
)

const relevantHighSchoolProducts = computed(() => {
  if (!hasHighSchoolGrades.value) return []
  return [...highSchoolProducts.value]
    .filter((product) => showAllHighSchoolProducts.value || isRelevantHighSchoolProduct(product))
    .sort(sortByNameGradeSubject)
})

const buildProductUrl = (product: Product) => {
  const { href } = router.resolve({
    name: 'product',
    params: {
      locationId: product.aunivers.locationId,
      slug: slugify(product.aunivers.name),
    },
  })
  return href
}

const constructHighSchoolContentUrl = (product: Product) => {
  if (!product.aunivers || !product.aunivers.urlAlias) {
    throw new Error('Could not construct high school content url')
  }
  const url = new URL(product.aunivers.urlAlias)
  return `${import.meta.env.VITE_AUNIVERS_SITEURL}${url.pathname}`
}
</script>
<template>
  <p
    v-if="relevantSubjects.length"
    class="mb-2 mt-4 font-medium"
    v-text="t('home.yourSubjects')"
  />
  <ul
    v-if="relevantSubjects.length"
    :id="TeleportationTarget.SubjectRowButtons.replace('#', '')"
    class="
          relative z-10 -mx-4 flex h-fit gap-3 overflow-x-scroll
          px-4 pb-4 xs:flex-wrap xs:overflow-x-visible xs:pb-0 md:max-w-[70%]
        "
  >
    <li
      v-for="subject of relevantSubjects"
      :key="subject"
    >
      <RouterLink
        :to="{ name: 'subject', params: { subject: subject.toLowerCase() } }"
        class="inline-flex rounded-full transition focus-visible:ring"
      >
        <KsButton
          variant="secondary"
          type="label"
          shape="rounded"
          class="!bg-white hover:!bg-seagreen-20 sm:text-base"
        >
          {{ t(`metadata.subjects.${subject}`) }}
        </KsButton>
      </RouterLink>
    </li>
    <li>
      <KsButton
        variant="tertiary"
        shape="rounded"
        :icon-left="showAllSubjects ? 'minus' : 'plus'"
        class="!text-white hover:!text-seagreen-50"
        @click="showAllSubjects = !showAllSubjects"
      >
        {{ showAllSubjects ? t('home.showLessSubjects') : t('home.showMoreSubjects') }}
      </KsButton>
    </li>
  </ul>
  <p
    v-if="relevantHighSchoolProducts.length"
    class="mb-2 mt-6 font-medium"
    v-text="t('home.highSchoolProducts')"
  />
  <ul
    v-if="relevantHighSchoolProducts.length"
    class="relative z-10 -mx-4 flex h-fit gap-3 overflow-x-scroll px-4 pb-4 xs:flex-wrap xs:overflow-x-visible xs:pb-0 md:max-w-[70%]"
  >
    <li
      v-for="product in relevantHighSchoolProducts"
      :key="product.ean"
    >
      <KsButton
        variant="secondary"
        shape="rounded"
        class="!bg-white hover:!bg-seagreen-20 sm:text-base"
        :href="constructHighSchoolContentUrl(product)"
      >
        {{ product.aunivers.name }}
      </KsButton>
    </li>
    <li v-if="showAllHighSchoolProducts || highSchoolProducts.length > relevantHighSchoolProducts.length">
      <KsButton
        variant="tertiary"
        shape="rounded"
        :icon-left="showAllHighSchoolProducts ? 'minus' : 'plus'"
        class="!text-white hover:!text-seagreen-50 sm:text-base"
        @click="showAllHighSchoolProducts = !showAllHighSchoolProducts"
      >
        {{ showAllHighSchoolProducts ? t('home.showLessProducts') : t('home.showMoreProducts') }}
      </KsButton>
    </li>
  </ul>

  <p
    v-if="relevantAddonProducts.length"
    class="mb-2 mt-6 font-medium"
    v-text="t('home.yourAddons')"
  />
  <ul
    v-if="relevantAddonProducts.length"
    class="relative z-10 -mx-4 flex h-fit gap-3 overflow-x-scroll px-4 pb-4 xs:flex-wrap xs:overflow-x-visible xs:pb-0 md:max-w-[70%]"
  >
    <li
      v-for="addon in relevantAddonProducts"
      :key="addon.ean"
    >
      <RouterLink
        :to="{ path: buildProductUrl(addon) }"
        class="inline-flex rounded-full transition focus-visible:ring"
      >
        <KsButton
          variant="secondary"
          type="label"
          shape="rounded"
          class="!bg-white hover:!bg-seagreen-20 sm:text-base"
        >
          {{ addon.aunivers.name }}
        </KsButton>
      </RouterLink>
    </li>
    <li v-if="showAllAddonProducts || addonProducts.length > relevantAddonProducts.length">
      <KsButton
        variant="tertiary"
        shape="rounded"
        :icon-left="showAllAddonProducts ? 'minus' : 'plus'"
        class="!text-white hover:!text-seagreen-50 sm:text-base"
        @click="showAllAddonProducts = !showAllAddonProducts"
      >
        {{ showAllAddonProducts ? t('home.showLessProducts') : t('home.showMoreProducts') }}
      </KsButton>
    </li>
  </ul>
</template>
